import { supabase } from "@/integrations/supabase/client";

import type { SocialLinkPlatform } from "../../shared/support";

type SocialLinkClickInput = {
  userId?: string | null;
  sourceScreen: string;
  destinationPlatform: SocialLinkPlatform;
  destinationUrl: string;
};

const SESSION_STORAGE_KEY = "kindred_social_link_session_id";
const RECENT_EVENT_WINDOW_MS = 5_000;
const recentEventMap = new Map<string, number>();

function pruneRecentEvents() {
  const now = Date.now();
  for (const [key, seenAt] of recentEventMap.entries()) {
    if (now - seenAt > RECENT_EVENT_WINDOW_MS) {
      recentEventMap.delete(key);
    }
  }
}

function buildFingerprint(input: SocialLinkClickInput) {
  return [input.userId || "", input.sourceScreen, input.destinationPlatform, input.destinationUrl].join("::");
}

function createSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `kindred-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getSessionId() {
  if (typeof window === "undefined") return createSessionId();

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) return existing;

  const next = createSessionId();
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, next);
  return next;
}

export async function trackKindredSocialLinkClick(input: SocialLinkClickInput) {
  if (!input.sourceScreen.trim() || !input.destinationUrl.trim()) return;

  pruneRecentEvents();
  const fingerprint = buildFingerprint(input);
  if (recentEventMap.has(fingerprint)) return;
  recentEventMap.set(fingerprint, Date.now());

  try {
    await supabase.from("social_link_clicks" as any).insert({
      user_id: input.userId || null,
      session_id: getSessionId(),
      app_name: "kindred",
      source_screen: input.sourceScreen.trim(),
      destination_platform: input.destinationPlatform,
      destination_url: input.destinationUrl.trim(),
      surface: "web",
    });
  } catch (error) {
    console.error("Kindred social link tracking failed", error);
  }
}
